import { useState, useMemo, useCallback } from 'react';
import despia from 'despia-native';
import { Capacitor } from '@capacitor/core';

// FIX: Detect native environment synchronously at module evaluation time.
// The original code ran this inside a useEffect, which meant isNativeApp
// started as `false` on every render and only updated after the effect fired.
// Any haptic/ad/native call that happened before the effect ran silently
// skipped (because isNativeApp was still false). Since both
// navigator.userAgent and Capacitor.isNativePlatform() are synchronous,
// there is no reason to defer this to a useEffect.
const detectIsNative = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const isDespia         = navigator.userAgent.toLowerCase().includes('despia');
  const isCapacitorNative = Capacitor.isNativePlatform();
  return isDespia || isCapacitorNative;
};

// Evaluate once at module load — stable for the lifetime of the app
const IS_NATIVE = detectIsNative();

export const useDespia = () => {
  // FIX: Synchronous initializer — isNativeApp is correct from the very first
  // render, not after a useEffect async round-trip. No useEffect needed.
  const [isNativeApp] = useState<boolean>(() => {
    const isNative = IS_NATIVE;
    console.log('[Despia SDK] isNativeApp:', isNative,
      '| platform:', Capacitor.getPlatform(),
      '| UA:', navigator.userAgent);
    return isNative;
  });

  // ── Core bridge ─────────────────────────────────────────────────────────────
  const executeCommand = useCallback(async (command: string, variables?: string[]) => {
    try {
      if (variables && variables.length > 0) {
        return await despia(command, variables);
      }
      return await despia(command);
    } catch (error) {
      console.error('[Despia] Command error:', error);
      return null;
    }
  }, []);

  // ── Haptics ─────────────────────────────────────────────────────────────────
  // FIX: Memoized so consumers can use haptic as a stable dependency reference
  const haptic = useMemo(() => ({
    light: async () => {
      if (!isNativeApp) return;
      try { await despia('lighthaptic://'); }
      catch (e) { console.error('[Despia] haptic.light error:', e); }
    },
    heavy: async () => {
      if (!isNativeApp) return;
      try { await despia('heavyhaptic://'); }
      catch (e) { console.error('[Despia] haptic.heavy error:', e); }
    },
    success: async () => {
      if (!isNativeApp) return;
      try { await despia('successhaptic://'); }
      catch (e) { console.error('[Despia] haptic.success error:', e); }
    },
    warning: async () => {
      if (!isNativeApp) return;
      try { await despia('warninghaptic://'); }
      catch (e) { console.error('[Despia] haptic.warning error:', e); }
    },
    error: async () => {
      if (!isNativeApp) return;
      try { await despia('errorhaptic://'); }
      catch (e) { console.error('[Despia] haptic.error error:', e); }
    },
  }), [isNativeApp]);

  // ── Ad display ──────────────────────────────────────────────────────────────
  // FIX: Memoized so useAdRequirement can pass it as a stable useCallback dep
  const displayRewardedAd = useCallback(async () => {
    if (!isNativeApp) {
      console.log('[Despia Ad] Skipped — not native');
      return null;
    }
    try {
      console.log('[Despia Ad] Displaying rewarded ad...');
      await despia('displayrewardedad://');
      console.log('[Despia Ad] Command sent');
      return true;
    } catch (error) {
      console.error('[Despia Ad] Failed:', error);
      const msg = error instanceof Error ? error.message : 'Unknown error';
      window.dispatchEvent(new CustomEvent('rewardedAdFailed', {
        detail: { error: msg, source: 'displayRewardedAd' },
      }));
      return null;
    }
  }, [isNativeApp]);

  // ── App info ─────────────────────────────────────────────────────────────────
  const getAppVersion = useCallback(() =>
    executeCommand('getappversion://', ['versionNumber', 'bundleNumber']),
  [executeCommand]);

  const getOneSignalPlayerId = useCallback(() =>
    executeCommand('getonesignalplayerid://', ['onesignalplayerid']),
  [executeCommand]);

  // ── UI helpers ───────────────────────────────────────────────────────────────
  const showSpinner = useCallback(() => executeCommand('spinneron://'),  [executeCommand]);
  const hideSpinner = useCallback(() => executeCommand('spinneroff://'), [executeCommand]);

  const setStatusBarColor = useCallback((r: number, g: number, b: number) => {
    executeCommand(`statusbarcolor://{${r}, ${g}, ${b}}`);
  }, [executeCommand]);

  const shareApp = useCallback((message: string, url: string) => {
    executeCommand(
      `shareapp://message?=${encodeURIComponent(message)}&url=${encodeURIComponent(url)}`
    );
  }, [executeCommand]);

  const saveImage = useCallback((imageUrl: string) => {
    executeCommand(`savethisimage://?url=${encodeURIComponent(imageUrl)}`);
  }, [executeCommand]);

  const openAppSettings         = useCallback(() => executeCommand('settingsapp://'),              [executeCommand]);
  const requestBiometricAuth    = useCallback(() => executeCommand('bioauth://'),                  [executeCommand]);
  const requestCameraPermission = useCallback(() => executeCommand('requestcamerapermission://'),  [executeCommand]);
  const requestPhotoLibraryPermission = useCallback(() =>
    executeCommand('requestphotolibrarypermission://'), [executeCommand]);

  // ── Subscription management ──────────────────────────────────────────────────
  const manageSubscriptions = useCallback(async () => {
    try {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios') {
        window.open('https://apps.apple.com/account/subscriptions', '_system');
      } else if (platform === 'android') {
        window.open('https://play.google.com/store/account/subscriptions', '_system');
      } else {
        await executeCommand('settingsapp://');
      }
    } catch (error) {
      console.error('[Despia] manageSubscriptions error:', error);
      try { await executeCommand('settingsapp://'); } catch {}
    }
  }, [executeCommand]);

  // ── Clipboard ────────────────────────────────────────────────────────────────

  const getClipboard = useCallback(async (): Promise<string | null> => {
    if (!isNativeApp) return null;
    try {
      const result = await executeCommand('getclipboard://', ['clipboarddata']);
      if (result && typeof result === 'object' && 'clipboarddata' in result) {
        return (result as Record<string, unknown>).clipboarddata as string;
      }
      return null;
    } catch (error) {
      console.error('[Despia] getClipboard error:', error);
      return null;
    }
  }, [isNativeApp, executeCommand]);

  // Deprecated — use HTML5 file input with <label> for all platforms (including iOS WKWebView)
  const pickPhotos = useCallback(async (
    _limit: number
  ): Promise<Array<{ blob: Blob; name: string; previewUrl: string }> | null> => {
    console.log('[pickPhotos] Deprecated — use HTML5 file input instead');
    return null;
  }, []);

  return {
    isNativeApp,
    executeCommand,
    haptic,
    getAppVersion,
    getOneSignalPlayerId,
    showSpinner,
    hideSpinner,
    setStatusBarColor,
    shareApp,
    saveImage,
    requestBiometricAuth,
    requestCameraPermission,
    requestPhotoLibraryPermission,
    manageSubscriptions,
    openAppSettings,
    displayRewardedAd,
    getClipboard,
    pickPhotos,
  };
};
